import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { ToastService } from '@rma/dashboard/generic/ui/toast';
import { HardNavigationService } from '@rma/generic/util/posthog';
import { catchError, filter, from, interval, map, merge, Observable, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PromptRefreshService {
  private readonly hardNavigation = inject(HardNavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly toastService = inject(ToastService);
  private readonly zone = inject(NgZone);

  private initialised = false;

  public initialise() {
    if (this.initialised || !this.swUpdate.isEnabled) {
      return;
    }

    this.initialised = true;

    // The timer runs outside Angular so a 15-minute tick never triggers change detection,
    // but the toast is rendered by change detection, so showing it has to re-enter the zone.
    this.zone.runOutsideAngular(() => {
      const fifteenMinutes = 15 * 60 * 1000;
      const newVersion$ = this.getNewVersionCheck();
      const manualCheck$ = interval(fifteenMinutes).pipe(switchMap(() => this.checkForUpdate()));

      merge(newVersion$, manualCheck$)
        .pipe(
          catchError(() => of(false)),
          filter((newVersion) => newVersion),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.zone.run(() => this.promptUpdate()));
    });
  }

  private promptUpdate(): void {
    this.toastService.showSuccessToastWithButton(() => this.applyUpdate(), 'Update', 'New version available.', true);
  }

  private applyUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => this.hardNavigation.reload())
      .catch((error) => {
        console.error('Failed to apply updates:', error);
        this.hardNavigation.reload();
      });
  }

  private checkForUpdate(): Observable<boolean> {
    return from(this.swUpdate.checkForUpdate()).pipe(catchError(() => of(false)));
  }

  private getNewVersionCheck(): Observable<boolean> {
    return this.swUpdate.versionUpdates.pipe(
      map((evt) => {
        switch (evt.type) {
          case 'VERSION_READY':
            return true;
          default:
          case 'VERSION_DETECTED':
          case 'VERSION_INSTALLATION_FAILED':
            return false;
        }
      }),
    );
  }
}
